
import React, { useCallback, useMemo, useState } from "react";
import { FlatList, LayoutChangeEvent, Text, View } from "react-native";
import { AddCircle } from "iconsax-react-nativejs";
import { useTranslation } from "react-i18next";
import PostCard from "../posts/post-card";
import ScButton from "../ui-kit/button";
import ScSearchInput from "../ui-kit/search-bar";
import { Post } from "@/src/models/post.model";
import CreatePostModal from "../posts/post-creation-modal";
import { usePosts } from "@/src/hooks/features/posts/use-posts";
import { EventItem, GetEventsResponse } from "@/src/models/event.model";
import EventCard from "../events/event-card";
import { useGetEventsQuery } from "@/src/services/api/events/events.hooks";
import { CONTAINER_PADDING, GAP } from "@/src/screens/events/events";
import StateDisplay from "../ui-kit/state-display";
import EventCardSkeleton from "../events/event-card-skeleton";
import { useRouter } from "expo-router";
import { useGetUserArchivedEventIdsQuery, useUpdateUserArchivedEventIdsMutation } from "@/src/services/api/profile/profile.hooks";
import { useAuth } from "@/src/contexts/auth.context";

type GroupEventsProps = Readonly<{
  groupId: string;
}>;


const getColumns = (width: number): number => {
  if (width >= 700) return 3;
  if (width >= 400) return 2;
  return 1;
};

const getCardWidth = (containerWidth: number, columns: number): number =>
  (containerWidth - GAP * (columns - 1)) / columns;

const GroupEvents = ({
  groupId
}: GroupEventsProps) => {
  const { t } = useTranslation();
  const router = useRouter();
  const { user } = useAuth();
  const currentUserId = user?.itemId ?? "";

  const [columns, setColumns] = useState(1);

  const [cardWidth, setCardWidth] = useState(0);
  const [eventToDelete, setEventToDelete] = useState<EventItem | null>(null);


  const {
    data,
    isLoading,
    isFetching,
    isFetchingNextPage,
    isError,
    refetch,
    fetchNextPage,
    hasNextPage,
  } = useGetEventsQuery({
    where: { groupIds: { eq: groupId } },
    order: [{ field: "startTime", direction: "ASC" }],
    pageSize: 10
  });

  const allEvents = useMemo(
    () =>
      data?.pages.flatMap(
        (page: GetEventsResponse["getevents"]) => page.items,
      ) ?? [],
    [data?.pages],
  );

  const { data: archivedEventIds = [] } =
    useGetUserArchivedEventIdsQuery(currentUserId);

  const { mutate: updateUserProfile } = useUpdateUserArchivedEventIdsMutation();


  const handleEventPress = useCallback((event: EventItem) => {
    router.push(`/events/${event.ItemId}`);
  }, []);

  const handleEditEvent = useCallback((event: EventItem) => {
    router.push({
      pathname: "/events/[id]/edit",
      params: { id: event.ItemId },
    });
  }, []);

  const handleArchiveEvent = useCallback(
    (event: EventItem) => {
      const isArchived = archivedEventIds.includes(event.ItemId);
      const updated = isArchived
        ? archivedEventIds.filter((id: string) => id !== event.ItemId)
        : [...archivedEventIds, event.ItemId];
      updateUserProfile({
        CreatedBy: currentUserId,
        archivedEventIds: updated,
      });
    },
    [archivedEventIds, currentUserId, updateUserProfile],
  );


  const handleEndReached = useCallback(() => {
    if (!isFetching && hasNextPage) fetchNextPage();
  }, [isFetching, hasNextPage, fetchNextPage]);

  const handleLayout = useCallback((e: LayoutChangeEvent) => {
    const width = e.nativeEvent.layout.width - CONTAINER_PADDING;
    const cols = getColumns(width);
    setColumns(cols);
    setCardWidth(getCardWidth(width, cols));
  }, []);

  const renderItem = useCallback(
    ({ item: event }: { item: EventItem }) => (
      <View style={{ width: cardWidth }}>
        <EventCard
          event={event}
          showJoin={false}
          onPress={handleEventPress}
          onEdit={handleEditEvent}
          onDelete={setEventToDelete}
          onArchive={handleArchiveEvent}
          isArchived={archivedEventIds.includes(event.ItemId)}
          isOwner={event.CreatedBy === currentUserId}
        />
      </View>
    ),
    [
      cardWidth,
      false, // showJoin
      handleEditEvent,
      archivedEventIds,
      handleArchiveEvent,
      currentUserId,
      handleEventPress,
    ],
  );


  const renderFooter = useCallback(() => {
    if (!isFetchingNextPage) return null;
    return (
      <>
        {Array.from({ length: columns }).map((_, idx) => (
          <View key={idx} style={{ width: cardWidth }}>
            <EventCardSkeleton />
          </View>
        ))}
      </>
    );
  }, [isFetchingNextPage, columns, cardWidth]);
  return (
    <View className="flex-1">
      <FlatList
        onLayout={handleLayout}
        key={columns}
        data={allEvents}
        keyExtractor={(event) => event.ItemId}
        numColumns={columns}
        columnWrapperStyle={columns > 1 ? { gap: GAP } : undefined}
        contentContainerStyle={{ gap: GAP, flexGrow: 1 }}
        ListEmptyComponent={
          <View className="flex-1 items-center justify-center">
            <StateDisplay
              descriptionLabel="LABEL_NO_EVENTS_DESC"
              imgSource={require("@/src/assets/images/empty.png")}
            />
          </View>
        }
        ListFooterComponent={renderFooter}
        onEndReachedThreshold={0.3}
        onEndReached={handleEndReached}
        showsVerticalScrollIndicator={false}
        renderItem={renderItem}
      />
    </View>


  );
};

export default GroupEvents;
